import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import {
  ArrowUpRight, GraduationCap, BookOpen, Users, RefreshCw, Pencil,
  Search, SlidersHorizontal, X,
} from "lucide-react";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import FilterSidebar from "../components/opportunities/FilterSidebar";
import { useAuth } from "../context/AuthContext";
import { getCoeExternalUrl } from "../lib/coeExternalUrls";
import CoeLabCardLink from "../components/CoeLabCardLink";

export default function LMSPublic() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scDomains, setScDomains] = useState([]);
  const [q, setQ] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [filters, setFilters] = useState({
    sc_domain: [],
    category: [],
    difficulty: [],
  });

  const loadLabs = useCallback(() => {
    setLoading(true); setError(false);
    api.get("/lms/labs")
      .then(r => setLabs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadLabs(); }, [loadLabs]);
  useEffect(() => {
    api.get("/governance/domains").then(r => setScDomains(r.data.domains || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return labs.filter((l) => {
      if (filters.sc_domain.length && !filters.sc_domain.includes(l.code)) return false;
      if (filters.category.length && !filters.category.includes(l.category)) return false;
      if (filters.difficulty.length && !filters.difficulty.includes(l.difficulty || "intermediate")) return false;
      if (q) {
        const hay = `${l.name} ${l.code} ${l.tagline || ""} ${(l.skills || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [labs, filters, q]);

  const sections = useMemo(() => {
    const categoryOptions = [...new Set(labs.map(l => l.category).filter(Boolean))]
      .map(c => ({ value: c, label: c }));
    return [
      { key: "sc_domain", label: "SC Domain", options: scDomains.map(d => ({ value: d.id, label: d.name })) },
      { key: "category", label: "Category", options: categoryOptions },
      { key: "difficulty", label: "Difficulty", options: [
        { value: "beginner",     label: "Beginner" },
        { value: "intermediate", label: "Intermediate" },
        { value: "advanced",     label: "Advanced" },
      ] },
    ];
  }, [labs, scDomains]);

  const onFilter = useCallback((key, next) => setFilters(p => ({ ...p, [key]: next })), []);
  const onClearAll = useCallback(() => setFilters({ sc_domain: [], category: [], difficulty: [] }), []);
  const activeFilterCount = Object.values(filters).reduce((n, v) => n + v.length, 0);

  return (
    <div className="min-h-screen bg-paper">
      <PublicNavBar primaryCta={{ to: "/signup?role=student", label: "Get started" }} />

      {/* Hero */}
      <section className="bg-brand-gradient text-white border-b border-rule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10 sm:py-14 md:py-20">
          <span className="eyebrow on-grad-muted">Centers of Excellence · LMS</span>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight" data-testid="lms-hero-title">
            Train where industry trains.
            <br />
            <span className="opacity-90 font-medium text-2xl sm:text-3xl md:text-4xl">Structured pathways. One placement engine.</span>
          </h1>
          <p className="text-base md:text-lg on-grad-muted max-w-2xl mt-4 leading-relaxed">
            Every lab is a structured pathway — videos, primers, projects and proctored quizzes. Finish a lab, earn a verifiable certificate and pick up a &ldquo;Trained at CoE&rdquo; badge that recruiters see on your applications.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup?role=student" className="btn-ghost-dark justify-center" data-testid="lms-cta-signup">Start learning</Link>
            <a href="#labs" className="btn-ghost-dark justify-center">Browse labs ↓</a>
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-b border-rule bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-3 divide-x divide-rule">
          {[
            [labs.length || 10, "CoE Labs", BookOpen],
            [labs.reduce((a, l) => a + (l.module_count || 0), 0) || "—", "Active Modules", GraduationCap],
            [labs.reduce((a, l) => a + (l.enrolled_count || 0), 0), "Enrolled Learners", Users],
          ].map(([k, v, Icon]) => (
            <div key={v} className="px-4 sm:px-6 py-6 sm:py-7 text-center">
              <Icon size={20} className="text-signal mx-auto mb-1.5" strokeWidth={1.6} />
              <div className="font-display font-black text-2xl md:text-3xl">{k}</div>
              <div className="eyebrow mt-1.5">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Search band */}
      <section className="bg-white border-b border-rule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              <input
                className="w-full pl-10 pr-3 py-2.5 rounded-md border border-rule bg-white text-sm focus:border-signal focus:ring-2 focus:ring-signal/20 outline-none transition"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search labs — e.g. Cybersecurity, VLSI, EV Mobility"
                data-testid="lms-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body grid */}
      <main id="labs" className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <FilterSidebar
                sections={sections}
                values={filters}
                onChange={onFilter}
                onClearAll={onClearAll}
                defaultOpen={["sc_domain", "category"]}
              />
            </div>
          </div>

          {/* Grid */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-slate-900" data-testid="lms-lab-count">{filtered.length}</span>
                {" "}lab{filtered.length === 1 ? "" : "s"}
                {q && <span className="text-muted"> for &ldquo;{q}&rdquo;</span>}
              </div>
              <button
                type="button"
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rule text-sm font-semibold hover:border-signal transition"
                data-testid="lms-mobile-filter-toggle"
              >
                <SlidersHorizontal size={14} />
                Filters
                {activeFilterCount > 0 && (
                  <span className="text-[10px] font-bold bg-signal text-white rounded-full px-1.5 min-w-[16px] text-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lms-lab-grid">
              {loading && Array.from({ length: 6 }).map((_, idx) => (
                <div key={`lms-skel-${idx}`} className="bg-white border border-rule rounded-xl h-64 animate-pulse" data-testid="lms-lab-skeleton" />
              ))}
              {!loading && filtered.map(l => {
                // If we have an external partner-LMS URL for this CoE code,
                // clicking the card opens that URL in a new tab. Otherwise
                // we keep the internal /lms/labs/{id} detail route.
                const extUrl = getCoeExternalUrl(l.code);
                const cardBase = "block bg-white border border-rule rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-md hover:border-signal/40 transition";
                const CardInner = (
                  <>
                    {l.cover_image ? (
                      <div className="relative h-36 overflow-hidden">
                        <img src={l.cover_image} alt={l.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${l.color}00 40%, ${l.color}cc 100%)` }} />
                        <span className="absolute top-3 left-3 font-mono text-xs px-2 py-1 rounded bg-white/95"
                          style={{ color: l.color, border: `1px solid ${l.color}40` }}>
                          {l.code}
                        </span>
                        <span className="absolute top-3 right-3 font-mono text-[10px] tracking-widest uppercase text-white bg-black/40 px-2 py-1 rounded">
                          {l.category}
                        </span>
                      </div>
                    ) : (
                      <div className="h-1.5" style={{ background: l.color }} />
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      {!l.cover_image && (
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs px-2 py-1 rounded"
                            style={{ background: `${l.color}15`, color: l.color, border: `1px solid ${l.color}40` }}>
                            {l.code}
                          </span>
                          <span className="eyebrow">{l.category}</span>
                        </div>
                      )}
                      <h3 className={`font-display font-bold text-lg leading-snug ${l.cover_image ? "" : "mt-3"}`}>{l.name}</h3>
                      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-2">{l.tagline || l.overview}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(l.skills || []).slice(0, 3).map(s => (
                          <span key={s} className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700">{s}</span>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-rule flex items-center justify-end text-xs text-muted">
                        <span className="text-signal font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          Explore <ArrowUpRight size={12} />
                        </span>
                      </div>
                    </div>
                  </>
                );
                return (
                  <div key={l.id} className="relative group" data-testid={`lab-card-wrap-${l.code}`}>
                    {isAdmin && (
                      <Link
                        to="/admin/coe-labs"
                        data-testid={`admin-edit-lab-${l.code}`}
                        title="Edit lab (admin)"
                        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 backdrop-blur grid place-items-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                      >
                        <Pencil size={15} className="text-signal" />
                      </Link>
                    )}
                    {extUrl ? (
                      <CoeLabCardLink
                        labCode={l.code}
                        fallbackInternalHref={`/lms/labs/${l.id}`}
                        className={cardBase}
                        testid={`lab-card-${l.code}`}
                      >
                        {CardInner}
                      </CoeLabCardLink>
                    ) : (
                      <Link
                        to={`/lms/labs/${l.id}`}
                        className={cardBase}
                        data-testid={`lab-card-${l.code}`}
                      >
                        {CardInner}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            {!loading && filtered.length === 0 && (
              <div className="bg-white border border-rule rounded-xl p-10 text-center" data-testid="lms-empty">
                <BookOpen size={32} className="mx-auto text-muted opacity-50" />
                <div className="mt-3 font-display font-bold text-base">No labs match your filters</div>
                <p className="text-sm text-muted mt-1">Try clearing filters or a different search term.</p>
                <button
                  type="button"
                  onClick={() => { onClearAll(); setQ(""); }}
                  className="mt-4 inline-flex px-4 py-2 rounded-md text-sm font-semibold text-signal border border-signal/40 hover:bg-signal hover:text-white transition"
                >
                  Reset filters
                </button>
              </div>
            )}

            {error && !loading && (
              <div className="mt-4 max-w-md mx-auto text-center" data-testid="lms-labs-error">
                <div className="bg-white border border-rule rounded-xl p-6">
                  <div className="text-sm font-medium">Couldn&apos;t load labs right now.</div>
                  <div className="text-xs text-muted mt-1">Check your connection and try again.</div>
                  <button
                    type="button"
                    onClick={loadLabs}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-signal text-white text-xs font-bold uppercase tracking-wider hover:-translate-y-0.5 active:scale-95 transition-all"
                    data-testid="lms-labs-retry"
                  >
                    <RefreshCw size={14} /> Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bridge band */}
      <section className="border-t border-rule bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
          <span className="eyebrow text-accent">The placement bridge</span>
          <p className="font-display font-medium text-xl sm:text-2xl md:text-3xl mt-3 leading-tight text-ink">
            Finish a lab → your profile picks up a <span className="text-signal font-bold">&ldquo;Trained at CoE-XX&rdquo;</span> badge that recruiters see when you apply.
          </p>
          <Link to="/signup?role=student" className="btn-primary mt-6 inline-flex">
            Create your learner profile <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <PublicFooter />

      {/* Mobile filter bottom sheet */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="lms-mobile-filter-sheet">
          <button
            type="button"
            aria-label="Close filters"
            onClick={() => setShowMobileFilters(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <div
            className="absolute bottom-0 inset-x-0 max-h-[85vh] bg-white rounded-t-2xl overflow-hidden flex flex-col"
            style={{ animation: "slideInFromRight 240ms ease-out" }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-rule">
              <div className="font-semibold text-sm inline-flex items-center gap-2">
                <SlidersHorizontal size={15} className="text-signal" /> Filters
              </div>
              <button
                type="button"
                onClick={() => setShowMobileFilters(false)}
                className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <FilterSidebar
                sections={sections}
                values={filters}
                onChange={onFilter}
                onClearAll={onClearAll}
                defaultOpen={["sc_domain", "category"]}
              />
            </div>
            <div className="border-t border-rule p-3 flex gap-2">
              <button
                type="button"
                onClick={onClearAll}
                className="flex-1 text-sm font-semibold py-2.5 rounded-md border border-rule hover:bg-slate-50 transition"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 text-sm font-semibold py-2.5 rounded-md text-white transition"
                style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
              >
                Show {filtered.length} lab{filtered.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
